import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TableSortLabel,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { StagedUser, CrossAccountWarning } from '../services/HxcStagingService';

type SortableColumn = 'accountName' | 'sfdcAccountId' | 'firstName' | 'lastName' | 'email';
type SortDirection = 'asc' | 'desc' | null;

interface StagingTableProps {
  users: StagedUser[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onRefresh: () => void;
  onEdit: (user: StagedUser) => void;
  onDelete: (user: StagedUser) => void;
  crossAccountWarnings?: Map<string, CrossAccountWarning>;
}

const StagingTable: React.FC<StagingTableProps> = ({
  users,
  selectedIds,
  onSelectionChange,
  onRefresh,
  onEdit,
  onDelete,
  crossAccountWarnings
}) => {
  const theme = useTheme();
  
  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<StagedUser | null>(null);
  
  // State for sorting
  const [sortColumn, setSortColumn] = useState<SortableColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  
  // Find duplicate emails
  const emailCounts = users.reduce((acc, user) => {
    const email = user.email.toLowerCase();
    acc[email] = (acc[email] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  // Helper function to check if user has duplicate email
  const hasDuplicateEmail = (user: StagedUser) => {
    return emailCounts[user.email.toLowerCase()] > 1;
  };
  
  // Helper function to check if SFDC Account ID is malformed
  const hasMalformedSfdcId = (user: StagedUser): boolean => {
    const sfdcId = user.sfdcAccountId;
    // Check if NOT exactly 15 characters OR contains non-alphanumeric characters
    return sfdcId.length !== 15 || !/^[a-zA-Z0-9]+$/.test(sfdcId);
  };
  
  // Helper function to check if first name is empty
  const hasEmptyFirstName = (user: StagedUser): boolean => {
    return !user.firstName || user.firstName.trim() === '';
  };
  
  // Helper function to check if last name is empty
  const hasEmptyLastName = (user: StagedUser): boolean => {
    return !user.lastName || user.lastName.trim() === '';
  };
  
  // Helper function to check if account name exceeds 80 characters
  const hasAccountNameTooLong = (user: StagedUser): boolean => {
    return user.accountName.length > 80;
  };
  
  // Build lookup maps for validation checks
  // Map of accountName -> first SFDC Account ID encountered
  const accountNameToSfdcId = users.reduce((acc, user) => {
    const accountName = user.accountName.toLowerCase();
    if (!acc[accountName]) {
      acc[accountName] = user.sfdcAccountId;
    }
    return acc;
  }, {} as Record<string, string>);
  
  // Map of SFDC ID -> first account info encountered (for duplicate SFDC ID check)
  const sfdcIdToAccountInfo = users.reduce((acc, user) => {
    const sfdcId = user.sfdcAccountId;
    if (!acc[sfdcId]) {
      acc[sfdcId] = {
        accountName: user.accountName.toLowerCase(),
        userId: user.id
      };
    }
    return acc;
  }, {} as Record<string, { accountName: string; userId: string }>);
  
  // Helper function to check if user has mismatched SFDC Account ID
  // (same account name, different SFDC ID)
  const hasSfdcIdMismatch = (user: StagedUser): boolean => {
    const accountName = user.accountName.toLowerCase();
    const expectedSfdcId = accountNameToSfdcId[accountName];
    return (expectedSfdcId && (expectedSfdcId !== user.sfdcAccountId)) || false;
  };
  
  // Helper function to check if user has duplicate SFDC Account ID
  // (different account name, same SFDC ID - first by ID order is truthful)
  const hasDuplicateSfdcId = (user: StagedUser): boolean => {
    const sfdcId = user.sfdcAccountId;
    const firstAccount = sfdcIdToAccountInfo[sfdcId];
    // If this SFDC ID exists with a different account name AND this is not the first occurrence
    return firstAccount 
      && firstAccount.accountName !== user.accountName.toLowerCase() 
      && firstAccount.userId !== user.id;
  };
  
  // Helper function to check if user is selectable (no issues)
  const isUserSelectable = (user: StagedUser): boolean => {
    return !hasDuplicateEmail(user) 
      && !hasSfdcIdMismatch(user) 
      && !hasDuplicateSfdcId(user)
      && !hasMalformedSfdcId(user)
      && !hasEmptyFirstName(user)
      && !hasEmptyLastName(user)
      && !hasAccountNameTooLong(user);
  };
  
  // Get list of selectable users
  const selectableUsers = users.filter(isUserSelectable);
  
  // Handle sort
  const handleSort = (column: SortableColumn) => {
    if (sortColumn === column) {
      // Cycle through: asc -> desc -> null (unsorted)
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      }
    } else {
      // New column: start with ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  
  // Sort users
  const sortedUsers = React.useMemo(() => {
    if (!sortColumn || !sortDirection) {
      return users; // Return unsorted (by id)
    }
    
    return [...users].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];
      
      // Handle null/undefined values
      if (!aValue) aValue = '';
      if (!bValue) bValue = '';
      
      // Convert to lowercase for case-insensitive sorting
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      if (aStr < bStr) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aStr > bStr) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [users, sortColumn, sortDirection]);
  
  // Handle select all checkbox
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Only select users that don't have issues
      onSelectionChange(selectableUsers.map(user => user.id));
    } else {
      onSelectionChange([]);
    }
  };
  
  // Handle individual checkbox toggle
  const handleToggle = (id: string) => {
    const currentIndex = selectedIds.indexOf(id);
    const newSelected = [...selectedIds];

    if (currentIndex === -1) {
      newSelected.push(id);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    onSelectionChange(newSelected);
  };

  const isSelected = (id: string) => selectedIds.indexOf(id) !== -1;
  const allSelected = selectableUsers.length > 0 && selectedIds.length === selectableUsers.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < selectableUsers.length;

  // Header cell styling
  const headerCellStyle = {
    fontWeight: 600, 
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.025em',
    py: 1.5,
    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
    backgroundColor: theme.palette.background.default,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04)
    },
    '& .MuiTableSortLabel-root': {
      color: theme.palette.text.primary,
      '&:hover': {
        color: theme.palette.text.primary,
      },
      '&.Mui-active': {
        color: theme.palette.text.primary,
      }
    }
  };

  // Handle opening delete confirmation dialog
  const handleOpenDeleteDialog = (user: StagedUser) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  // Handle closing delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  // Handle confirming delete
  const handleConfirmDelete = () => {
    if (userToDelete) {
      onDelete(userToDelete);
      handleCloseDeleteDialog();
    }
  };

  return (
    <>
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" sx={headerCellStyle}>
              <Checkbox
                indeterminate={someSelected}
                checked={allSelected}
                onChange={handleSelectAll}
              />
            </TableCell>
            <TableCell sx={headerCellStyle}>
              <TableSortLabel
                active={sortColumn === 'accountName'}
                direction={sortColumn === 'accountName' ? sortDirection || 'asc' : 'asc'}
                onClick={() => handleSort('accountName')}
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    color: sortColumn === 'accountName' ? `${theme.palette.primary.main} !important` : undefined
                  }
                }}
              >
                Account Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellStyle}>
              <TableSortLabel
                active={sortColumn === 'sfdcAccountId'}
                direction={sortColumn === 'sfdcAccountId' ? sortDirection || 'asc' : 'asc'}
                onClick={() => handleSort('sfdcAccountId')}
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    color: sortColumn === 'sfdcAccountId' ? `${theme.palette.primary.main} !important` : undefined
                  }
                }}
              >
                SFDC Account ID
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellStyle}>
              <TableSortLabel
                active={sortColumn === 'firstName'}
                direction={sortColumn === 'firstName' ? sortDirection || 'asc' : 'asc'}
                onClick={() => handleSort('firstName')}
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    color: sortColumn === 'firstName' ? `${theme.palette.primary.main} !important` : undefined
                  }
                }}
              >
                First Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellStyle}>
              <TableSortLabel
                active={sortColumn === 'lastName'}
                direction={sortColumn === 'lastName' ? sortDirection || 'asc' : 'asc'}
                onClick={() => handleSort('lastName')}
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    color: sortColumn === 'lastName' ? `${theme.palette.primary.main} !important` : undefined
                  }
                }}
              >
                Last Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellStyle}>
              <TableSortLabel
                active={sortColumn === 'email'}
                direction={sortColumn === 'email' ? sortDirection || 'asc' : 'asc'}
                onClick={() => handleSort('email')}
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    color: sortColumn === 'email' ? `${theme.palette.primary.main} !important` : undefined
                  }
                }}
              >
                Email
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sx={headerCellStyle}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedUsers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography variant="body2" color="textSecondary">
                  No users in staging
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            sortedUsers.map((user) => {
              const isItemSelected = isSelected(user.id);
              const isDuplicate = hasDuplicateEmail(user);
              const hasIdMismatch = hasSfdcIdMismatch(user);
              const hasDuplicateSfdc = hasDuplicateSfdcId(user);
              const hasMalformedId = hasMalformedSfdcId(user);
              const missingFirstName = hasEmptyFirstName(user);
              const missingLastName = hasEmptyLastName(user);
              const accountNameTooLong = hasAccountNameTooLong(user);
              
              // Determine row styling - all validation issues use error style (red)
              const hasError = hasMalformedId || hasDuplicateSfdc || hasIdMismatch || isDuplicate || missingFirstName || missingLastName || accountNameTooLong;
              const backgroundColor = hasError ? '#f4433644' : 'inherit';
              
              return (
                <TableRow
                  key={user.id}
                  hover
                  role="checkbox"
                  aria-checked={isItemSelected}
                  selected={isItemSelected}
                  sx={{
                    backgroundColor: backgroundColor,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: `${alpha(theme.palette.primary.light, 0.08)} !important`
                    }
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      disabled={isDuplicate || hasIdMismatch || hasDuplicateSfdc || hasMalformedId || missingFirstName || missingLastName || accountNameTooLong}
                      checked={isItemSelected}
                      onChange={() => handleToggle(user.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {user.accountName}
                      {crossAccountWarnings?.has(user.id) && (
                        <Tooltip title={`This user is already a member of "${crossAccountWarnings.get(user.id)!.existingAccountName}" (ID: ${crossAccountWarnings.get(user.id)!.existingAccountId})`}>
                          <WarningAmberIcon fontSize="small" color="warning" sx={{ flexShrink: 0 }} />
                        </Tooltip>
                      )}
                    </Box>
                    {accountNameTooLong && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: 'block', fontWeight: 'bold' }}
                      >
                        Max 80 Characters
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.sfdcAccountId}
                    {hasMalformedId ? (
                      <Typography 
                        variant="caption" 
                        color="error"
                        sx={{ display: 'block', fontWeight: 'bold' }}
                      >
                        Invalid SFDC ID
                      </Typography>
                    ) : hasDuplicateSfdc ? (
                      <Typography 
                        variant="caption" 
                        color="error"
                        sx={{ display: 'block', fontWeight: 'bold' }}
                      >
                        Duplicate SFDC ID
                      </Typography>
                    ) : hasIdMismatch ? (
                      <Typography 
                        variant="caption" 
                        color="error"
                        sx={{ display: 'block', fontWeight: 'bold' }}
                      >
                        ID Mismatch
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {user.firstName || ''}
                    {missingFirstName && (
                      <Typography 
                        variant="caption" 
                        color="error" 
                        sx={{ display: 'block', fontWeight: 'bold' }}
                      >
                        Required
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.lastName || ''}
                    {missingLastName && (
                      <Typography 
                        variant="caption" 
                        color="error" 
                        sx={{ display: 'block', fontWeight: 'bold' }}
                      >
                        Required
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.email}
                    {isDuplicate && (
                      <Typography 
                        variant="caption" 
                        color="error" 
                        sx={{ display: 'block', fontWeight: 'bold' }}
                      >
                        Duplicate email
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'nowrap' }}>
                      <IconButton
                        size="small"
                        onClick={() => onEdit(user)}
                        aria-label="edit user"
                        sx={{
                          color: 'primary.main',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(user)}
                        aria-label="delete user"
                        sx={{
                          color: 'error.main',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
    
    {/* Delete Confirmation Dialog */}
    <Dialog
      open={deleteDialogOpen}
      onClose={handleCloseDeleteDialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Confirm Delete User</DialogTitle>
      <DialogContent>
        {userToDelete && (
          <Box>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete this user? This action cannot be undone.
            </Typography>
            <Box sx={{ mt: 2, p: 2 }}>
              <Typography variant="body2"><strong>Account Name:</strong> {userToDelete.accountName}</Typography>
              <Typography variant="body2"><strong>SFDC Account ID:</strong> {userToDelete.sfdcAccountId}</Typography>
              <Typography variant="body2"><strong>First Name:</strong> {userToDelete.firstName}</Typography>
              <Typography variant="body2"><strong>Last Name:</strong> {userToDelete.lastName}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {userToDelete.email}</Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCloseDeleteDialog}>
          Cancel
        </Button>
        <Button 
          onClick={handleConfirmDelete}
          variant="contained"
          color="error"
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default StagingTable;
